import { useState } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Search, Star } from "lucide-react";

export default function LoyaltyLookup() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get("/loyalty/lookup", { params: { code: code.trim() } });
      setResult(data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="loyalty-lookup" className="mt-6 bg-white border border-border rounded-2xl p-6">
      <p className="font-display text-xl tracking-tight">Have a loyalty code? View your tier & rental history</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="loyalty-lookup-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="Loyalty code (EDL-XXXXXX) or your email"
          className="h-10 rounded-lg border border-input bg-white px-3 text-sm font-ui w-72 hover:border-primary/50 transition-colors duration-200"
        />
        <Button data-testid="loyalty-lookup-button" onClick={lookup} disabled={loading} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-10 px-5 text-sm">
          <Search size={14} className="mr-2" /> {loading ? "Checking…" : "View My Loyalty"}
        </Button>
      </div>
      {error && <p data-testid="loyalty-lookup-error" className="mt-3 text-sm text-destructive font-ui">{error}</p>}
      {result && (
        <div data-testid="loyalty-lookup-result" className="mt-6 border border-border rounded-xl p-5">
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-display text-2xl tracking-tight">{result.tier} <span className="gold-text">−{result.percent}%</span></p>
            <span data-testid="loyalty-lookup-member-no" className="text-[11px] font-ui tracking-[0.2em] uppercase text-muted-foreground">{result.member_no}</span>
          </div>
          <p data-testid="loyalty-lookup-rentals" className="mt-1 text-xs text-muted-foreground font-ui">
            Rented <span className="text-foreground font-semibold">{result.rentals}</span> {result.rentals === 1 ? "time" : "times"}
            {result.next_tier ? ` — ${result.rentals_to_next} more to ${result.next_tier}` : " — top tier"}
          </p>
          {result.perks && result.perks.length > 0 && (
            <ul data-testid="loyalty-lookup-perks" className="mt-3 space-y-1.5">
              {result.perks.map((p) => (
                <li key={p} className="flex items-start gap-1.5 text-xs font-ui text-foreground/80"><Star size={11} className="gold-text mt-0.5 shrink-0" /> {p}</li>
              ))}
            </ul>
          )}
          {result.history && result.history.length > 0 ? (
            <div className="mt-4 space-y-2">
              <p className="text-[11px] font-ui tracking-[0.2em] uppercase text-muted-foreground">Rental history</p>
              {result.history.map((h, i) => (
                <div key={h.booking_ref || i} data-testid={`loyalty-history-row-${i}`} className="flex flex-wrap items-center justify-between gap-2 text-sm font-ui border-b border-border/60 pb-2">
                  <span className="gold-text font-medium">{h.booking_ref}</span>
                  <span>{h.vehicle_name}</span>
                  <span className="text-muted-foreground text-xs">{fmtDate(h.start_date)} → {fmtDate(h.end_date)}</span>
                  <span className="capitalize text-xs text-muted-foreground">{(h.status || "").replace("_", " ")}</span>
                  <span>{fmtMoney(h.total)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground font-ui">No rentals yet — book your first ride to start earning.</p>
          )}
        </div>
      )}
    </div>
  );
}
